/**
 * Purchase Flow Configuration
 *
 * Single configuration object that bundles all "infrastructure" concerns:
 * rendering, theming, logging, analytics, and captcha.
 *
 * Passed as a single `config` prop to PurchaseFlow and made available to
 * all nested components via PurchaseFlowConfigProvider context.
 *
 * Individual hooks expose each concern:
 *   usePurchaseRender()   → { renderIcon, renderText }
 *   usePurchaseTheme()    → { theme, textColor }
 *   usePurchaseLog()      → log function
 *   usePurchaseAnalytics() → analytics service (existing)
 *   usePurchaseCaptcha()  → withCaptcha function
 */

import type React from 'react';
import type { AnalyticsConfig, OnAnalyticsEvent } from './analytics';
import type { OnLogEvent } from './logger';

// ============================================================================
// Theme
// ============================================================================

export type PurchaseFlowTheme = 'light' | 'dark';

/**
 * Narrows an arbitrary theme string to PurchaseFlowTheme.
 * Returns 'dark' only when the input is exactly 'dark'; otherwise 'light'.
 */
export const toPurchaseFlowTheme = (value: string | undefined): PurchaseFlowTheme => (value === 'dark' ? 'dark' : 'light');

// ============================================================================
// Captcha
// ============================================================================

/**
 * reCAPTCHA callback — loads captcha and invokes callback with token.
 * Injected from app (e.g., common/config/utils) — purchase flow does not import it directly.
 */
export type WithCaptchaFn = (action: string, callback: (rcToken: string) => void) => void;

// ============================================================================
// Config
// ============================================================================

export interface PurchaseFlowConfig {
  // ---- Rendering ----

  /**
   * Render function for icons. Receives an icon identifier string
   * (see PURCHASE_FLOW_ICONS for navigation icons, card type strings for payment methods)
   * and returns a React node.
   */
  renderIcon: (icon: string, className?: string) => React.ReactNode;

  /**
   * Render function for translated text. Receives a translation key string
   * (see PURCHASE_FLOW_TEXT_KEYS for all available keys) and optional params,
   * returns a plain string.
   */
  renderText: (key: string, params?: Record<string, string | number>) => string;

  // ---- Locale ----

  /**
   * Current app language code (e.g. 'en', 'es', 'pt').
   * Used by components that need locale-aware rendering
   * (e.g. Apple Pay native button).
   * Defaults to 'en' when not provided.
   */
  locale?: string;

  // ---- Theme ----

  /**
   * Visual theme. Affects gateway iframe styling (text color, etc.).
   * Defaults to 'light'.
   */
  theme?: PurchaseFlowTheme;

  // ---- Logging ----

  /**
   * Callback for log events. If not provided, logs are silently dropped.
   * @see OnLogEvent
   */
  onLog?: OnLogEvent;

  // ---- Analytics ----

  /** Analytics configuration (purchase screen, click source, etc.) */
  analytics?: AnalyticsConfig;

  /** Callback invoked for each analytics event */
  onAnalyticsEvent?: OnAnalyticsEvent;

  /** Optional page-visit callback (e.g. for "add new card draw"); used by payment sources flow */
  onTrackPageVisit?: (pageTitle: string) => void;

  // ---- Captcha ----

  /**
   * reCAPTCHA handler — required for API calls that need captcha token.
   * If not provided, captcha-gated calls will fail.
   */
  withCaptcha?: WithCaptchaFn;

  // ---- API Headers ----

  /**
   * Returns extra HTTP headers to include in every API request made by the
   * purchase flow.  May be async (e.g. to await `getDeviceId()`).
   *
   * Each host app provides its own implementation:
   *   - KA:    X-CSRF-TOKEN, WEBDEVICE_ID, Language-Code, WEB_CLIENT (KAWeb/PSWeb/PGWeb)
   *   - POWeb: X-CSRF-TOKEN, WEBDEVICE_ID, Language-Code, WEB_CLIENT (POWeb/POMBA)
   *
   * If not provided, requests are sent with only the default
   * Accept / Content-Type headers.
   */
  requestHeaders?: () => Record<string, string> | Promise<Record<string, string>>;

  // ---- Payment Options ----

  /**
   * Whether In-App Purchase (IAP) is available as a payment option.
   * When false, IAP is filtered out of available payment options.
   * Defaults to true.
   *
   * PO mobile apps set this to false when the device does not support
   * the new PO wallet (checked via `supportedNewPOWalletVersion`).
   */
  iapEnabled?: boolean;

  // ---- Purchase Rules ----

  /**
   * Minimum purchase amount enforced in additionalFunds mode.
   * When the diff (required - balance) is positive but below this amount,
   * the charge amount is bumped up to this value.
   * Defaults to $10.00 if not provided.
   *
   * Typically sourced from Redux: user.minCreditRequestedCents / 100.
   */
  minPurchaseAmount?: number;

  /**
   * User's current available credit (balance).
   * Used in additionalFunds mode to compute the diff (required - balance)
   * before the /combined API call, so the API receives the correct charge amount.
   *
   * Typically sourced from Redux: parseFloat(user.clientAvailableCredit) || 0.
   * Defaults to 0 if not provided.
   */
  clientAvailableCredit?: number;

}
